import React, { useEffect, useState } from "react";
import client, { apiErr, API } from "../../lib/api";
import { Panel, Spinner } from "../ui-kit";
import { Sparkles, Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { Field, Empty, inputCls, fmtDate } from "./shared";

const BASE = API.replace(/\/api$/, "");
const STYLES = [
  { v: "result", l: "Trade Result" }, { v: "promo", l: "VIP Promo" },
  { v: "education", l: "Educational" }, { v: "algo", l: "Algo Service" },
];
const ASPECTS = [{ v: "square", l: "Post 1:1" }, { v: "story", l: "Story 9:16" }, { v: "landscape", l: "Banner 16:9" }];

function Chips({ items, value, onChange, prefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((i) => (
        <button key={i.v} data-testid={`${prefix}-${i.v}`} onClick={() => onChange(i.v)}
          className={`px-3 py-1.5 text-xs border transition-colors duration-200 ${value === i.v ? "bg-gold text-black border-gold" : "border-[color:var(--border-strong)] text-slate-300 hover:text-white"}`}>{i.l}</button>
      ))}
    </div>
  );
}

function Gallery({ images }) {
  const copy = (u) => navigator.clipboard.writeText(u).then(() => toast.success("Image link copied")).catch(() => toast.error("Copy failed"));
  if (!images.length) return <Empty testid="studio-empty" text="Abhi koi image nahi bani. Left side se pehli image generate karein." />;
  return (
    <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 p-3">
      {images.map((im) => (
        <div key={im.id} data-testid={`studio-img-${im.id}`} className="surface group">
          <img src={`${BASE}${im.url}`} alt={im.headline || im.style} loading="lazy" className="w-full aspect-square object-cover" />
          <div className="p-2 flex items-center gap-2">
            <span className="text-[10px] text-slate-500 truncate">{im.style} · {fmtDate(im.created_at)}</span>
            <a data-testid={`download-img-${im.id}`} href={`${BASE}${im.url}`} download={`fatehfx-${im.id}.png`} target="_blank" rel="noreferrer" className="ml-auto text-slate-400 hover:text-gold"><Download size={14} /></a>
            <button data-testid={`copy-img-${im.id}`} onClick={() => copy(`${BASE}${im.url}`)} className="text-slate-400 hover:text-gold"><Copy size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MarketingStudio({ limit = 20 }) {
  const [f, setF] = useState({ style: "result", aspect: "square", signal: "", headline: "", extra: "" });
  const [sigs, setSigs] = useState({ vip: [], engine: [] });
  const [images, setImages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [used, setUsed] = useState(null);
  const load = () => client.get("/studio/images").then(({ data }) => setImages(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    load();
    client.get("/studio/signals").then(({ data }) => setSigs(data)).catch(() => {});
  }, []);
  const gen = async () => {
    setBusy(true);
    const [src, id] = f.signal ? f.signal.split(":") : [null, null];
    try {
      const { data } = await client.post("/studio/generate", { style: f.style, aspect: f.aspect, signal_id: id, signal_source: src, headline: f.headline, extra: f.extra }, { timeout: 180000 });
      setUsed(data.images_today); toast.success("Image ready"); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
      <Panel title="Create Marketing Image" testid="studio-form">
        <div className="p-4 space-y-4">
          <Field label="Style"><Chips items={STYLES} value={f.style} onChange={(v) => setF({ ...f, style: v })} prefix="style" /></Field>
          <Field label="Format"><Chips items={ASPECTS} value={f.aspect} onChange={(v) => setF({ ...f, aspect: v })} prefix="aspect" /></Field>
          <Field label="Based on past signal (optional)">
            <select data-testid="studio-signal" value={f.signal} onChange={(e) => setF({ ...f, signal: e.target.value })} className={inputCls}>
              <option value="">— None —</option>
              {sigs.vip.filter((t) => t.outcome).map((t) => <option key={t.id} value={`vip:${t.id}`} label={`VIP · ${t.instrument} ${t.direction} · ${t.net_pips} pips · ${t.closed_date || t.opened_date}`} />)}
              {sigs.engine.map((s) => <option key={s.id} value={`engine:${s.id}`} label={`Engine · ${s.instrument} ${s.direction} · ${s.status}`} />)}
            </select>
          </Field>
          <Field label="Headline (optional)"><input data-testid="studio-headline" value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} className={inputCls} placeholder="Gold +120 pips today" /></Field>
          <Field label="Extra instructions (optional)"><textarea data-testid="studio-extra" rows={3} value={f.extra} onChange={(e) => setF({ ...f, extra: e.target.value })} className={inputCls} placeholder="Add my WhatsApp number +91…" /></Field>
          <button data-testid="studio-generate-btn" disabled={busy} onClick={gen} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />Generating (~30s)…</> : <><Sparkles size={16} />Generate with Nano Banana</>}
          </button>
          <div data-testid="studio-quota" className="text-[11px] text-slate-500 text-center">{used != null ? `${used}/${limit} used today` : `Daily limit: ${limit} images`}</div>
        </div>
      </Panel>
      <Panel title="My Images" testid="studio-gallery">{!images ? <Spinner /> : <Gallery images={images} />}</Panel>
    </div>
  );
}
